import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { ArrowLeft, Clock, Users } from "lucide-react";
import { API } from "@/constants";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import Seo from "@/components/Seo";
import SmartImage from "@/components/SmartImage";

export default function RecipeDetailPage() {
  const { id } = useParams();
  const [recipe, setRecipe] = useState(null);
  const [missing, setMissing] = useState(false);

  useEffect(() => {
    setRecipe(null);
    setMissing(false);
    window.scrollTo(0, 0);
    axios.get(`${API}/recipes/${id}`).then((r) => setRecipe(r.data)).catch(() => setMissing(true));
  }, [id]);

  if (missing) {
    return (
      <main className="bg-white min-h-screen font-body">
        <Navbar />
        <div className="pt-40 pb-32 text-center px-4" data-testid="recipe-not-found">
          <h1 className="font-display font-bold text-3xl text-kera-ink">Recipe not found</h1>
          <Link to="/recipes" className="mt-6 inline-block rounded-full bg-black text-white px-6 py-3 text-sm hover:bg-kera-gold hover:text-black transition-colors">
            Back to recipes
          </Link>
        </div>
        <Footer />
      </main>
    );
  }

  if (!recipe) {
    return (
      <main className="bg-white min-h-screen font-body">
        <Navbar />
        <div className="pt-40 text-center font-mono text-xs uppercase tracking-[0.25em] text-kera-copper">Loading</div>
      </main>
    );
  }

  const ingredients = (recipe.ingredients || "").split("\n").map((s) => s.trim()).filter(Boolean);
  const steps = (recipe.instructions || "").split("\n").map((s) => s.trim()).filter(Boolean);

  return (
    <main className="bg-white text-kera-ink font-body min-h-screen" data-testid="recipe-detail-page">
      {recipe && <Seo title={recipe.title} description={recipe.description} path={`/recipes/${recipe.id}`} />}
      <Navbar />
      <div className="px-4 sm:px-8 lg:px-16 max-w-5xl mx-auto pt-16 pb-20">
        <Link to="/recipes" className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.2em] text-kera-muted hover:text-kera-copper transition-colors" data-testid="recipe-back">
          <ArrowLeft size={12} /> All Recipes
        </Link>

        <motion.h1
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          className="mt-6 font-display font-bold tracking-tight text-kera-ink text-3xl sm:text-4xl lg:text-5xl"
          data-testid="recipe-title"
        >
          {recipe.title}
        </motion.h1>
        {recipe.description && (
          <p className="mt-4 max-w-2xl text-base sm:text-lg text-kera-muted leading-relaxed">{recipe.description}</p>
        )}
        <div className="mt-5 flex gap-5 font-mono text-[11px] uppercase tracking-[0.18em] text-kera-copper">
          {recipe.prep_time && <span className="inline-flex items-center gap-1.5" data-testid="recipe-prep-time"><Clock size={13} /> {recipe.prep_time}</span>}
          {recipe.servings && <span className="inline-flex items-center gap-1.5" data-testid="recipe-servings"><Users size={13} /> Serves {recipe.servings}</span>}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.1 }}
          className="relative mt-8"
        >
          <div className="absolute -inset-3 border border-kera-gold/40 rounded-2xl translate-x-3 translate-y-3" />
          <SmartImage src={recipe.image} alt={recipe.title} eager sizes="100vw" className="relative rounded-2xl w-full h-[320px] sm:h-[440px] object-cover border border-kera-line" data-testid="recipe-image" />
        </motion.div>

        {recipe.products?.length > 0 && (
          <div className="mt-12 rounded-2xl border border-kera-gold/40 bg-[#FAF7F0] p-6" data-testid="recipe-products">
            <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-kera-copper mb-4">Made with KERA</p>
            <div className="flex flex-wrap gap-4">
              {recipe.products.map((p) => (
                <Link key={p.id} to={`/products/${p.id}`} className="group flex items-center gap-3 rounded-xl border border-kera-line bg-white p-2.5 pr-5 hover:border-kera-gold transition-colors" data-testid={`recipe-product-${p.id}`}>
                  <SmartImage src={p.image} alt={p.name} sizes="48px" className="h-12 w-12 rounded-lg object-cover border border-kera-line" />
                  <div>
                    <p className="font-mono text-[9px] uppercase tracking-[0.18em] text-kera-copper">{p.category}</p>
                    <p className="font-display font-semibold text-sm text-kera-ink group-hover:text-kera-copper transition-colors">KERA {p.name}</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}

        <div className="mt-12 grid grid-cols-1 md:grid-cols-5 gap-8">
          {ingredients.length > 0 && (
            <div className="md:col-span-2 rounded-2xl border border-kera-line bg-white p-6 h-fit" data-testid="recipe-ingredients">
              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-kera-copper mb-4">Ingredients</p>
              <ul className="space-y-2.5">
                {ingredients.map((ing, i) => (
                  <li key={i} className="flex gap-3 text-sm text-kera-muted leading-relaxed">
                    <span className="mt-2 h-1.5 w-1.5 rotate-45 bg-kera-gold shrink-0" />
                    {ing}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {steps.length > 0 && (
            <div className="md:col-span-3 rounded-2xl border border-kera-line bg-white p-6" data-testid="recipe-steps">
              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-kera-copper mb-4">Method</p>
              <ol className="space-y-4">
                {steps.map((step, i) => (
                  <li key={i} className="flex gap-3.5 text-sm sm:text-base text-kera-muted leading-relaxed">
                    <span className="shrink-0 h-7 w-7 rounded-full bg-black text-kera-gold text-xs font-mono flex items-center justify-center">
                      {i + 1}
                    </span>
                    {step}
                  </li>
                ))}
              </ol>
            </div>
          )}
        </div>
      </div>
      <Footer />
    </main>
  );
}
